import React, { useState, useEffect } from "react";
import ApiClient from "../ApiClient";
import { notify } from "../utils/ToastUtils";
import {
  Shield,
  ShieldCheck,
  KeyRound,
  Download,
  Copy,
  CheckCircle2,
  Check,
  AlertCircle,
  Smartphone,
  ChevronDown,
  ChevronUp,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";
import TailwindModal, { TailwindModalHeader, TailwindModalBody, TailwindModalFooter } from "./TailwindModal";
import TwoFactorFormUI from "./TwoFactorFormUI";
import DisableTwoFactorModal from "./DisableTwoFactorModal";

/* ── tiny OTP input: 6 individual boxes ── */
const OtpInput = ({ value, onChange }) => {
  const inputsRef = React.useRef([]);

  const handleChange = (idx, e) => {
    const val = e.target.value.replace(/\D/g, "").slice(-1);
    if (e.target.value && !val) return;
    let arr = (value || "").padEnd(6, " ").split("");
    arr[idx] = val || " ";
    onChange(arr.join("").slice(0, 6));
    if (val && idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    const valStr = (value || "").padEnd(6, " ");
    if (e.key === "Backspace" && valStr[idx] === " " && idx > 0) {
      e.preventDefault();
      let arr = valStr.split("");
      arr[idx - 1] = " ";
      onChange(arr.join("").slice(0, 6));
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < 5) {
      e.preventDefault();
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted) {
      let arr = (value || "").padEnd(6, " ").split("");
      for (let i = 0; i < pasted.length; i++) {
        arr[i] = pasted[i];
      }
      onChange(arr.join("").slice(0, 6));
      const focusIdx = Math.min(pasted.length, 5);
      inputsRef.current[focusIdx]?.focus();
    }
  };

  return (
    <div className="flex justify-between sm:justify-start sm:gap-2 lg:gap-3" onPaste={handlePaste}>
      {Array.from({ length: 6 }).map((_, i) => {
        const char = (value || "").padEnd(6, " ")[i];
        return (
          <input
            key={i}
            ref={(el) => (inputsRef.current[i] = el)}
            type="text"
            inputMode="numeric"
            maxLength={2}
            value={char === " " ? "" : char}
            onFocus={(e) => e.target.select()}
            onChange={(e) => handleChange(i, e)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            className="w-7 h-8 sm:w-9 sm:h-10 lg:w-9 lg:h-10 text-center text-base sm:text-lg lg:text-xl font-bold border-2 border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-slate-800 shadow-sm placeholder-slate-400"
            placeholder="•"
          />
        );
      })}
    </div>
  );
};

const TwoFactorSetup = ({
  isExpanded = false,
  onToggle = () => {},
  is2FAEnabled = false,
  onStatusChange = () => {},
}) => {
  // Disable 2FA modal state
  const [disableData, setDisableData] = useState({
    showModal: false,
    isDisabling: false,
    errorMessage: "",
  });

  // Setup / verification flow state
  const [setupData, setSetupData] = useState({
    qrCodeUrl: "",
    secret: "",
    verificationCode: "",
    secretCopied: false,
    verifying: false
  });

  // Recovery codes modal state
  const [recoveryData, setRecoveryData] = useState({
    showModal: false,
    password: "",
    totp: "",
    codes: [],
    copied: false,
    showPassword: false,
    isGenerating: false,
    errorMessage: ""
  });

  const handleEnableClick = async () => {
    try {
      const { data } = await ApiClient.post(
        `${process.env.REACT_APP_BASE_URL}/2fa/generate`,
      );
      setSetupData(prev => ({ ...prev, qrCodeUrl: data.qrCodeUrl, secret: data.secret }));
      if (!isExpanded) onToggle();
    } catch {
      notify.error("Failed to generate 2FA secret");
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setSetupData(prev => ({ ...prev, verifying: true }));
    try {
      await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/2fa/verify`, {
        token: (setupData.verificationCode || "").replace(/\s/g, ""),
        secret: setupData.secret,
      });
      notify.success("Two-Factor Authentication enabled successfully!");
      onStatusChange(true);
      setSetupData({
        qrCodeUrl: "",
        secret: "",
        verificationCode: "",
        secretCopied: false,
        verifying: false
      });
    } catch {
      notify.error("Invalid verification code. Please try again.");
      setSetupData(prev => ({ ...prev, verificationCode: "", verifying: false }));
    }
  };

  const handleConfirmDisable = async (finalCode, useRecovery, password) => {
    setDisableData(prev => ({ ...prev, isDisabling: true, errorMessage: "" }));
    try {
      await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/2fa/disable`, {
        code: finalCode,
        password: password
      });
      notify.success("Two-Factor Authentication disabled successfully.");
      onStatusChange(false);
      setDisableData({
        showModal: false,
        isDisabling: false,
        errorMessage: ""
      });
      setRecoveryData({
        showModal: false,
        password: "",
        totp: "",
        codes: [],
        copied: false,
        showPassword: false,
        isGenerating: false,
        errorMessage: ""
      });
      if (isExpanded) onToggle();
    } catch (error) {
      console.error("Failed to disable 2FA", error);
      let msg = error?.response?.data?.message || "Failed to disable 2FA. Please verify your code.";
      setDisableData(prev => ({ ...prev, isDisabling: false, errorMessage: msg }));
    }
  };

  const handleGenerateRecovery = async (e) => {
    if (e) e.preventDefault();

    if (!recoveryData.password) {
      setRecoveryData(prev => ({ ...prev, errorMessage: "Password is required." }));
      return;
    }

    const finalTotp = (recoveryData.totp || "").replace(/\s/g, "");

    if (!finalTotp) {
      setRecoveryData(prev => ({ ...prev, errorMessage: "Two-Factor Authentication code is required." }));
      return;
    }

    if (finalTotp.length !== 6) {
      setRecoveryData(prev => ({ ...prev, errorMessage: "Authentication code must be 6 digits." }));
      return;
    }

    setRecoveryData(prev => ({ ...prev, isGenerating: true, errorMessage: "" }));
    try {
      const { data } = await ApiClient.post(
        `${process.env.REACT_APP_BASE_URL}/2fa/generate-recovery`,
        { password: recoveryData.password, totp: finalTotp },
      );
      setRecoveryData(prev => ({
        ...prev,
        codes: data.recoveryCodes,
        password: "",
        totp: "",
        isGenerating: false,
        errorMessage: ""
      }));
    } catch (error) {
      console.error(error);
      let msg = error?.response?.data?.message || error?.response?.data?.error;
      if (!msg && typeof error?.response?.data === 'string' && !error.response.data.includes('<html')) {
        msg = error.response.data;
      }
      if (!msg) {
        msg = error?.response?.status === 404
          ? "Service endpoint not found (404)."
          : (error?.message || "Failed to generate recovery codes");
      }
      setRecoveryData(prev => ({ ...prev, isGenerating: false, errorMessage: msg }));
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(setupData.secret);
    setSetupData(prev => ({ ...prev, secretCopied: true }));
    setTimeout(() => setSetupData(prev => ({ ...prev, secretCopied: false })), 2000);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(recoveryData.codes.join("\n"));
    setRecoveryData(prev => ({ ...prev, copied: true }));
    setTimeout(() => setRecoveryData(prev => ({ ...prev, copied: false })), 2000);
  };

  const downloadAsText = () => {
    const el = document.createElement("a");
    el.href = URL.createObjectURL(
      new Blob([recoveryData.codes.join("\n")], { type: "text/plain" }),
    );
    el.download = "da-recovery-codes.txt";
    document.body.appendChild(el);
    el.click();
    document.body.removeChild(el);
  };

  return (
    <div
      className={`flex flex-col border border-slate-200 rounded-xl overflow-hidden transition-all ${isExpanded ? "border-t-4 border-t-blue-500 bg-slate-50/50 shadow-sm" : "bg-white"}`}
    >
      {/* ── Header Area ── */}
      <div
        className={`flex flex-col md:flex-row md:items-center justify-between gap-4 py-4 px-4 sm:px-5 transition-colors ${is2FAEnabled ? "cursor-pointer hover:bg-slate-50/80" : ""}`}
        onClick={() => {
          if (is2FAEnabled) onToggle();
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className={`p-2 rounded-lg shrink-0 ${is2FAEnabled ? "bg-emerald-50 text-emerald-600" : "bg-blue-50 text-blue-600"}`}
          >
            {is2FAEnabled ? <ShieldCheck size={20} /> : <Shield size={20} />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-800 text-sm">
                Two-Factor Authentication
              </h3>
              {is2FAEnabled ? (
                <></>
                // <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-50 border border-emerald-100 text-emerald-700 uppercase tracking-wider">
                //   <CheckCircle2 size={12} strokeWidth={2.5} /> ENABLED
                // </span>
              ) : (
                <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-50 border border-slate-200 text-slate-500 uppercase tracking-wider">
                  NOT ENABLED
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              {is2FAEnabled
                ? "Your account is securely protected with an authenticator app."
                : "Add an extra layer of security to your account."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0 sm:self-start md:self-center">
          {is2FAEnabled && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
            >
              {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          )}

          {!is2FAEnabled && !isExpanded && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEnableClick();
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors text-sm shadow-sm"
            >
              Enable 2FA
            </button>
          )}

        </div>
      </div>

      {/* ── Expanded Content Area ── */}
      {isExpanded && (
        <>
          {setupData.qrCodeUrl && (
            <div className="px-4 sm:px-5 pb-5 pt-2 border-t border-slate-100 bg-white">
              {/* ── QR Setup Area ── */}
              <div className="py-4">
                <div className="w-full">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                    {/* Step 1 */}
                    <div className="flex flex-col">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-7 h-7 text-sm rounded-full bg-blue-100 text-blue-600 font-bold flex items-center justify-center shrink-0">
                          1
                        </div>
                        <h3 className="text-base font-bold text-slate-800">
                          Scan the QR Code
                        </h3>
                      </div>
                     <p className="text-slate-600 text-[13px] mb-5 leading-relaxed">
                        Open your authenticator app (like{" "}
                        <span className="font-semibold text-slate-700">
                          Google Authenticator
                        </span>{" "}
                        or{" "}
                        <span className="font-semibold text-slate-700">
                          Microsoft Authenticator
                        </span>
                        ) and scan this QR code to add your account.
                      </p>

                      <div className="flex flex-col xl:flex-row items-center xl:items-start gap-5 bg-slate-50 p-4 rounded-xl border border-slate-100">
                        <div className="bg-white p-2.5 rounded-xl border border-slate-200 shadow-sm shrink-0">
                          <img
                            src={setupData.qrCodeUrl}
                            alt="2FA QR Code"
                            className="w-32 h-32"
                          />
                        </div>
                        <div className="w-full min-w-0">
                          <p className="text-[13px] font-semibold text-slate-700 mb-2">
                            Can't scan the code?
                          </p>
                          <p className="text-xs text-slate-500 mb-3">
                            Copy this setup key to manually enter it into your
                            app.
                          </p>
                          <div className="flex items-start gap-2 w-full">
                            <div className="flex-1 min-w-0 px-3 py-2.5 bg-white border border-slate-200 rounded-lg shadow-sm">
                              <p className="font-mono text-xs font-medium text-slate-700 break-all leading-relaxed">
                                {setupData.secret}
                              </p>
                            </div>
                            <button
                              onClick={copySecret}
                              title="Copy secret"
                              className="p-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-slate-600 shrink-0 shadow-sm active:scale-95"
                            >
                              {setupData.secretCopied ? (
                                <CheckCircle2
                                  size={18}
                                  className="text-emerald-600"
                                />
                              ) : (
                                <Copy size={18} />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="flex flex-col">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-7 h-7 text-sm rounded-full bg-blue-100 text-blue-600 font-bold flex items-center justify-center shrink-0">
                          2
                        </div>
                        <h3 className="text-base font-bold text-slate-800">
                          Verify Setup
                        </h3>
                      </div>
                      <p className="text-slate-600 text-[13px] mb-5 leading-relaxed">
                        Enter the 6-digit code generated by your authenticator
                        app to confirm the setup is complete.
                      </p>

                      <form
                        onSubmit={handleVerify}
                        className="flex flex-col gap-8"
                      >
                        <div className="pt-2">
                          <OtpInput
                            value={setupData.verificationCode}
                            onChange={(val) => setSetupData(prev => ({ ...prev, verificationCode: val }))}
                          />
                        </div>

                        <div className="flex flex-wrap items-center gap-4">
                          <button
                            type="submit"
                            disabled={(setupData.verificationCode || "").replace(/\s/g, "").length < 6 || setupData.verifying}
                            className="flex items-center justify-center px-6 py-2.5 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm shadow-[0_4px_14px_rgb(37,99,235,0.25)] hover:shadow-[0_6px_20px_rgb(37,99,235,0.35)]"
                          >
                            {setupData.verifying ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Verifying...
                              </>
                            ) : "Verify & Enable"}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setSetupData({
                                qrCodeUrl: "",
                                secret: "",
                                verificationCode: "",
                                secretCopied: false,
                                verifying: false
                              });
                              if (isExpanded) onToggle();
                            }}
                            className="px-5 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-lg font-bold hover:bg-slate-50 transition-colors text-sm shadow-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Enabled State Area ── */}
          {is2FAEnabled && (
            <div className="px-4 sm:px-5 border-t border-slate-100 bg-white">
              <div className="py-5">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Authenticator App Card */}
                  <div className="border border-slate-200 rounded-xl p-4 sm:p-5 bg-white shadow-sm flex flex-col justify-between gap-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-blue-50/80 text-blue-600 rounded-xl flex items-center justify-center shrink-0 border border-blue-100/50">
                          <Smartphone size={18} strokeWidth={2.2} />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-800 text-sm mb-0.5">
                            Authenticator App
                          </h4>
                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                            <span className="text-xs font-semibold text-emerald-600">Enabled</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => setDisableData({ showModal: true, isDisabling: false, errorMessage: "" })}
                          className="px-3.5 py-1.5 border border-red-200 text-red-600 bg-red-50/70 hover:bg-red-100/70 rounded-lg font-semibold text-xs transition-colors shadow-sm"
                        >
                          Disable
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Recovery Codes Card */}
                  <div className="border border-slate-200 rounded-xl p-4 sm:p-5 bg-white shadow-sm flex flex-col justify-between gap-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-amber-50/80 text-amber-600 rounded-xl flex items-center justify-center shrink-0 border border-amber-100/50">
                          <KeyRound size={18} strokeWidth={2.2} />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-800 text-sm mb-0.5">
                            Recovery Codes
                          </h4>
                          <p className="text-xs text-slate-500">
                            Backup access to your account.
                          </p>
                        </div>
                      </div>
                      {!recoveryData.showModal && recoveryData.codes.length === 0 && (
                        <button
                          onClick={() => setRecoveryData(prev => ({ ...prev, showModal: true, errorMessage: "", password: "", totp: "", codes: [] }))}
                          className="px-4 py-1.5 bg-blue-600 text-white rounded-lg font-semibold text-xs hover:bg-blue-700 transition-colors shadow-sm shrink-0"
                        >
                          Generate
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Generate Recovery Codes Modal ── */}
      <TailwindModal 
        isOpen={recoveryData.showModal || recoveryData.codes.length > 0} 
        toggle={() => setRecoveryData(prev => ({ ...prev, showModal: false, codes: [], errorMessage: "", password: "", totp: "" }))} 
        maxWidth={"445px"}
      >
        <TailwindModalHeader 
          title="Generate Recovery Codes" 
          toggle={() => setRecoveryData(prev => ({ ...prev, showModal: false, codes: [], errorMessage: "", password: "", totp: "" }))} 
        />
        <TailwindModalBody>
        <div>
          <div className="flex items-center justify-center mb-6 mt-1">
            <div className="flex items-center">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold bg-blue-600 text-white shadow-sm">
                {recoveryData.codes.length > 0 ? <Check size={14} strokeWidth={3} /> : "1"}
              </div>
              <span className={`ml-2 text-[13px] font-bold ${recoveryData.codes.length > 0 ? "text-slate-400" : "text-slate-800"}`}>
                Verify
              </span>
            </div>
            <div className="w-8 h-px bg-slate-200 mx-3" />
            <div className="flex items-center">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold shadow-sm ${recoveryData.codes.length > 0 ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-400"}`}>
                2
              </div>
              <span className={`ml-2 text-[13px] font-bold ${recoveryData.codes.length > 0 ? "text-slate-800" : "text-slate-400"}`}>
                View Codes
              </span>
            </div>
          </div>

          {recoveryData.codes.length === 0 ? (
            <>
              <div className="flex flex-col items-center text-center mb-4">
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-3">
                  <Lock size={20} /> 
                </div>
                <h4 className="font-bold text-lg text-slate-800 mb-1.5">Verify it's you</h4>
                <p className="text-[13px] text-slate-500 px-4 leading-relaxed">
                  For your security, please verify your identity<br/>before generating new recovery codes.
                </p>
              </div>

              <form id="verify-identity-form" onSubmit={handleGenerateRecovery} noValidate className="flex flex-col">
                <div className="mb-4">
                  <label className="block text-[13px] font-bold text-slate-800 mb-1.5">
                    Account Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input 
                      type={recoveryData.showPassword ? "text" : "password"} 
                      value={recoveryData.password} 
                      onChange={e => setRecoveryData(prev => ({ ...prev, password: e.target.value, errorMessage: "" }))} 
                      className="w-full pl-10 pr-10 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm transition-colors placeholder-slate-400" 
                      placeholder="Enter your password" 
                    />
                    <button 
                      type="button" 
                      onClick={() => setRecoveryData(prev => ({ ...prev, showPassword: !prev.showPassword }))} 
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {recoveryData.showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-[13px] font-bold text-slate-800 mb-1">
                    Authenticator Code <span className="text-red-500">*</span>
                  </label>
                  <p className="text-[13px] text-slate-500 mb-3">
                    Enter the 6-digit code from your authenticator app.
                  </p>
                  <div className="flex justify-center gap-2 sm:gap-2.5" onPaste={(e) => {
                      e.preventDefault();
                      const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
                      setRecoveryData(prev => ({ ...prev, totp: pasted, errorMessage: "" }));
                  }}>
                    {Array.from({ length: 6 }).map((_, i) => {
                      const char = (recoveryData.totp || "").padEnd(6, " ")[i];
                      return (
                        <input
                          key={i}
                          id={`rec-otp-${i}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={2}
                          value={char === " " ? "" : char}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, "").slice(-1);
                            if (e.target.value && !val) return;
                            let arr = (recoveryData.totp || "").padEnd(6, " ").split("");
                            arr[i] = val || " ";
                            const next = arr.join("").slice(0, 6);
                            setRecoveryData(prev => ({ ...prev, totp: next, errorMessage: "" }));
                            if (val && i < 5) document.getElementById(`rec-otp-${i + 1}`)?.focus();
                          }}
                          onKeyDown={(e) => {
                            const valStr = (recoveryData.totp || "").padEnd(6, " ");
                            if (e.key === "Backspace" && valStr[i] === " " && i > 0) {
                              e.preventDefault();
                              let arr = valStr.split("");
                              arr[i - 1] = " ";
                              const next = arr.join("").slice(0, 6);
                              setRecoveryData(prev => ({ ...prev, totp: next, errorMessage: "" }));
                              document.getElementById(`rec-otp-${i - 1}`)?.focus();
                            } else if (e.key === "ArrowLeft" && i > 0) {
                              e.preventDefault();
                              document.getElementById(`rec-otp-${i - 1}`)?.focus();
                            } else if (e.key === "ArrowRight" && i < 5) {
                              e.preventDefault();
                              document.getElementById(`rec-otp-${i + 1}`)?.focus();
                            }
                          }}
                          className="w-[42px] h-[42px] sm:w-[46px] sm:h-[46px] text-center text-xl font-bold text-slate-800 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors shadow-sm placeholder-slate-400"
                          placeholder="•"
                        />
                      );
                    })}
                  </div>
                </div>

                {recoveryData.errorMessage && (
                  <div className="mt-3 text-[13px] font-medium text-red-600 bg-red-50 px-3 py-2.5 rounded-lg border border-red-100 flex items-center gap-2">
                    <AlertCircle size={16} className="shrink-0" />
                    <span>{recoveryData.errorMessage}</span>
                  </div>
                )}
              </form>
            </>
          ) : (
            <>
              <div className="flex flex-col items-center text-center mb-5">
                <div className="w-12 h-12 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mb-3">
                  <Check size={24} strokeWidth={2.5} />
                </div>
                <h4 className="font-bold text-lg text-slate-800 mb-1.5">
                  Recovery codes generated
                </h4>
                <p className="text-[13px] text-slate-500 px-2 leading-relaxed">
                  Save these codes securely. They won't be shown again.
                </p>
              </div>

              <div className="bg-[#FFF8F0] border border-[#FFE8D6] rounded-lg px-3.5 py-3 flex gap-2 items-center justify-center mb-5">
                <AlertCircle size={18} className="text-orange-500 shrink-0" fill="#F97316" color="#FFF8F0" />
                <span className="font-bold text-[#9A5016] text-[13px]">
                  Keep these codes private.
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 p-3.5 rounded-xl border border-slate-100 bg-slate-50/50 mb-2">
                {recoveryData.codes.map((c, i) => (
                  <div
                    key={i}
                    className="font-mono text-[13px] font-bold tracking-[0.15em] text-slate-800 bg-white py-3 px-2 text-center rounded-lg border border-slate-200 shadow-sm select-all"
                  >
                    {c.slice(0, 4)} {c.slice(4)}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        </TailwindModalBody>

        <TailwindModalFooter>
          {recoveryData.codes.length === 0 ? (
            <div className="flex items-center w-full justify-between gap-3">
              <button 
                type="button" 
                onClick={() => setRecoveryData(prev => ({ ...prev, showModal: false }))} 
                className="flex-1 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-lg font-bold hover:bg-slate-50 transition-colors text-[13px] shadow-sm"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                form="verify-identity-form" 
                disabled={recoveryData.isGenerating} 
                className="flex items-center justify-center flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors disabled:opacity-50 text-[13px] shadow-[0_2px_10px_rgba(37,99,235,0.25)]"
              >
                {recoveryData.isGenerating ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </>
                ) : "Continue"}
              </button>
            </div>
          ) : (
            <div className="flex items-center w-full gap-2.5">
              <button onClick={downloadAsText} className="flex-[0.8] flex items-center justify-center gap-1.5 px-3 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-lg font-bold hover:bg-slate-50 transition-colors text-[13px] shadow-sm">
                 <Download size={16} /> Download
              </button>
              <button onClick={copyToClipboard} className="flex-[0.8] flex items-center justify-center gap-1.5 px-3 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-lg font-bold hover:bg-slate-50 transition-colors text-[13px] shadow-sm">
                 {recoveryData.copied ? <CheckCircle2 size={16} className="text-emerald-600"/> : <Copy size={16} />} 
                 {recoveryData.copied ? "Copied" : "Copy"}
              </button>
              <button 
                onClick={() => setRecoveryData(prev => ({ ...prev, codes: [], showModal: false }))} 
                className="flex-[1.2] px-3 py-2.5 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors text-[13px] shadow-[0_2px_10px_rgba(37,99,235,0.25)]"
              >
                I've saved them
              </button>
            </div>
          )}
        </TailwindModalFooter>
      </TailwindModal>

      {/* ── Disable 2FA Verification Modal ── */}
      <DisableTwoFactorModal
        isOpen={disableData.showModal}
        onClose={() => setDisableData(prev => ({ ...prev, showModal: false, errorMessage: "" }))}
        onSubmit={handleConfirmDisable}
        loading={disableData.isDisabling}
        error={disableData.errorMessage}
      />
    </div>
  );
};

export default TwoFactorSetup;
